import { useState, useSyncExternalStore } from 'react';
import { useIntlayer } from 'react-intlayer';

import { LocalizedLink } from '~/components/LocalizedLink';
import { Button } from '~/components/ui/button';

const COOKIE_CONSENT_STORAGE_KEY = 'trypillian-cookie-consent';

type CookieConsentValue = 'accepted' | 'declined';

type CookiePolicyPopupContent = {
  accept: string;
  ariaLabel: string;
  cookiePolicy: string;
  decline: string;
  messageEnd: string;
  messageMiddle: string;
  messageStart: string;
  privacyPolicy: string;
};

const subscribeToCookieConsent = () => () => {};

const getCookieConsentVisibility = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === null;
  } catch {
    return true;
  }
};

const getServerCookieConsentVisibility = () => false;

export const CookiePolicyPopup = () => {
  const content = useIntlayer(
    'cookie-policy-popup'
  ) as unknown as CookiePolicyPopupContent;
  const shouldShowConsent = useSyncExternalStore(
    subscribeToCookieConsent,
    getCookieConsentVisibility,
    getServerCookieConsentVisibility
  );
  const [isDismissed, setIsDismissed] = useState(false);
  const isVisible = shouldShowConsent && !isDismissed;

  const saveChoice = (value: CookieConsentValue) => {
    try {
      window.localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        JSON.stringify({
          value,
          createdAt: new Date().toISOString(),
        })
      );
    } catch {
      // Storage can be blocked in strict privacy modes; still dismiss for this session.
    }

    setIsDismissed(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <section
      aria-label={content.ariaLabel}
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] bg-black"
    >
      <div className="mx-auto flex max-w-[1680px] flex-col items-start justify-center gap-9 bg-black p-6 md:flex-row md:flex-wrap md:items-center md:px-8 md:py-6">
        <p className="w-full flex-none font-fragment-mono text-[13px] leading-normal text-white uppercase md:min-w-0 md:flex-1">
          <span>{content.messageStart}</span>
          <LocalizedLink
            className="underline underline-offset-2 transition-colors hover:text-[#0e4be0] focus-visible:text-[#0e4be0] focus-visible:outline-none"
            to="/privacy"
          >
            {content.privacyPolicy}
          </LocalizedLink>
          <span>{content.messageMiddle}</span>
          <LocalizedLink
            className="underline underline-offset-2 transition-colors hover:text-[#0e4be0] focus-visible:text-[#0e4be0] focus-visible:outline-none"
            to="/cookies"
          >
            {content.cookiePolicy}
          </LocalizedLink>
          <span>{content.messageEnd}</span>
        </p>

        <div className="flex w-full shrink-0 gap-4 md:w-[224px]">
          <Button
            className="flex-1 tracking-[0.13px]"
            size="sm"
            type="button"
            variant="default"
            onClick={() => saveChoice('declined')}
          >
            {content.decline}
          </Button>
          <Button
            className="flex-1 border-white/16 bg-blue-primary tracking-[0.13px] backdrop-blur-[5px] hover:bg-[#0e4be0] md:border-transparent md:bg-white/[0.08] md:hover:border-white/16 md:hover:bg-blue-primary"
            size="sm"
            type="button"
            variant="glass"
            onClick={() => saveChoice('accepted')}
          >
            {content.accept}
          </Button>
        </div>
      </div>
    </section>
  );
};
